// **HOW FAR APART TWO WORDS ARE — ONE DECLARATION, TWO CALLERS.**
//
// This was a private `within` inside `src/emailAddress.js` and it is LIFTED here because a SECOND
// caller needs it (`server/seal-truth-gate.js`, for her misspelled-seal item). That is this house's
// own lifting rule — `balanced-body.mjs` and `jsx-tree.mjs` were lifted the same way, when a second
// call site arrived rather than on the day somebody thought it looked shared — and two spellings of
// *how far apart are two words* is two answer keys (R28/R29), with the copy that drifts deciding
// whether a member's garbled seal is reported as a garble at all.
//
// >>> **DAMERAU, NOT PLAIN LEVENSHTEIN, AND HER OWN TWO EXAMPLES ARE WHY.** <<< Her signup round
// named `gamil` -> `gmail`, a TRANSPOSITION of two adjacent letters, which plain Levenshtein scores
// as TWO edits — so a distance-1 reader answers NOTHING on the exact typo she named. The same shape
// is the commonest thing an image model does to a word it is lettering by hand, and the seal corpus
// carries it: `hte`, `teh`, `adn`. One transposition is ONE edit here.
//
// IT IS CAPPED AND THE CAP IS WHAT MAKES IT CHEAP: a row whose best cell already exceeds `max` can
// never come back under it, so the walk stops rather than filling a full matrix for a pair that is
// obviously unrelated. It answers `max + 1` for anything past the cap, so a caller compares against
// `max` and never against an exact distance it did not ask for.
//
// It imports NOTHING, which is the entry requirement that lets a FAST fixture LOAD and RUN it.
export function within(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const rows = [Array.from({ length: b.length + 1 }, (_, i) => i)];
  for (let i = 1; i <= a.length; i += 1) {
    const row = [i];
    let best = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(rows[i - 1][j] + 1, row[j - 1] + 1, rows[i - 1][j - 1] + cost);
      // The transposition arm: `ab` and `ba` are one edit apart, not two.
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, rows[i - 2][j - 2] + 1);
      row[j] = v;
      if (v < best) best = v;
    }
    if (best > max) return max + 1;
    rows.push(row);
  }
  return rows[a.length][b.length];
}
